import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from '@/components/feature/AuthGuard';
import OgShell from './components/OgShell';
import { QiAuthForm } from './components/AuthGate';
import { useQiRole } from './lib/useQiRole';
import { fetchMyCases, getMyNotifications, getPractitionerDashboard, listMyQiCaseSeries } from './lib/api';
import PractitionerProfile from './components/PractitionerProfile';
import { CASE_STATUS_LABELS, formatDate } from './lib/constants';
import type { MyNotificationRow, PractitionerDashboardRow, QiCase, QiCaseSeries } from './lib/types';

export default function OgQiHealingEntry() {
  const auth = useAuthState();
  const { role } = useQiRole(auth.user);
  const navigate = useNavigate();
  const [myCases, setMyCases] = useState<QiCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportToken, setReportToken] = useState('');
  const [dashboard, setDashboard] = useState<PractitionerDashboardRow[]>([]);
  const [myNotifications, setMyNotifications] = useState<MyNotificationRow[]>([]);
  const [caseSeries, setCaseSeries] = useState<QiCaseSeries[]>([]);
  // 施炁者可以一次看見所有尚未送出的暫存案例；已列於暫存或 Dashboard 的案例不重複顯示。
  const practitionerDraftCases = myCases.filter(
    (c) => c.status === 'draft' && c.practitioner_id === auth.user?.id,
  );
  const dashboardCaseIds = new Set(dashboard.map((row) => row.case_id));
  const practitionerDraftIds = new Set(practitionerDraftCases.map((c) => c.id));
  const caseSeriesWithCases = new Set(myCases.map((c) => c.case_series_id).filter(Boolean));
  const pendingCaseSeries = caseSeries.filter(
    (series) => series.status === 'active' && !caseSeriesWithCases.has(series.id),
  );
  const ownerOnlyCases = myCases.filter(
    (c) => !dashboardCaseIds.has(c.id) && !practitionerDraftIds.has(c.id),
  );

  useEffect(() => {
    if (!auth.user) {
      setMyCases([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMyCases(auth.user.id)
      .then((data) => {
        if (!cancelled) setMyCases(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : '載入失敗');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auth.user]);

  const handleReportLink = () => {
    const raw = reportToken.trim();
    if (!raw) return;
    const token = raw.includes('/') ? raw.split('/').filter(Boolean).pop() : raw;
    if (token) navigate(`/og/qi-healing/follow-up/${token}`);
  };

  // 尚未建立首筆 qi_cases 的主訴系列，也要讓施炁者能繼續處理。
  useEffect(() => {
    if (!auth.user || (role !== 'practitioner' && role !== 'candidate')) {
      setCaseSeries([]);
      return;
    }
    listMyQiCaseSeries().then(setCaseSeries).catch(() => setCaseSeries([]));
  }, [auth.user, role]);

  // 施炁者 Dashboard 聚合
  useEffect(() => {
    if (!auth.user || role !== 'practitioner') return;
    getPractitionerDashboard().then(setDashboard).catch(() => {});
  }, [auth.user, role]);

  // 案主站內通知
  useEffect(() => {
    if (!auth.user) return;
    getMyNotifications().then(setMyNotifications).catch(() => {});
  }, [auth.user]);

  return (
    <OgShell>
      <div className="max-w-5xl mx-auto px-4 md:px-6 space-y-10">
        {/* Hero */}
        <section className="text-center pt-6 md:pt-10 space-y-4">
          <span className="inline-flex items-center gap-1.5 text-xs text-accent-700 bg-accent-100/70 px-3 py-1 rounded-full">
            <i className="ri-flashlight-line" />
            OG 一之門 &middot; 實作與見證
          </span>
          <h1 className="text-2xl md:text-4xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            宇炁療育登錄
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            保存宇炁施作過程、當事人感受與後續變化，形成可覆核的見證紀錄。
          </p>
          {!auth.user && (
            <div className="flex items-center justify-center gap-3 flex-wrap pt-1">
              <Link
                to={`/account?returnTo=${encodeURIComponent('/og/qi-healing')}`}
                className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg bg-accent-500 text-background-50 text-sm md:text-base hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-login-box-line text-lg" />
                登入 AUM1001
              </Link>
            </div>
          )}
        </section>

        {/* 未登入：三個主要入口（公開，避免訪客誤以為可立即成為施炁者） */}
        {!auth.user && (
          <section className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link
                to="/og/qi-healing/intake"
                className="group flex flex-col items-start gap-3 bg-background-100 border border-accent-200 rounded-xl p-5 hover:border-accent-400 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-xl text-primary-700" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">我要登錄／申請服務</p>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                    本人建立申請、選擇正式授權服務者與希望的方式、都市及時段。
                  </p>
                </div>
              </Link>

              <Link
                to="/og/qi-healing"
                className="group flex flex-col items-start gap-3 bg-background-100 border border-foreground-200/50 rounded-xl p-5 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-add-line text-xl text-accent-700" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">施炁者登入／建立案例</p>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                    需具正式授權資格者登入後才能建立宇炁案例。
                  </p>
                </div>
              </Link>

              <Link
                to="/og/qi-healing/witnesses"
                className="group flex flex-col items-start gap-3 bg-background-100 border border-foreground-200/50 rounded-xl p-5 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-eye-line text-xl text-secondary-900" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">查看匿名見證</p>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                    瀏覽已核准公開、去識別化的宇炁見證。
                  </p>
                </div>
              </Link>
            </div>

            <div className="text-center pt-1">
              <Link
                to="/og/portal"
                className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-foreground-500 hover:text-accent-600 cursor-pointer transition-colors"
              >
                <i className="ri-information-line" />
                了解如何由 RG／HEB1 進入子場
              </Link>
            </div>
          </section>
        )}

        {/* 未登入：登入 / 註冊表單（置頂，顯著） */}
        {!auth.user && (
          <section id="auth" className="scroll-mt-24 space-y-4">
            <div className="text-center">
              <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                登入 AUM1001
              </h2>
              <p className="text-xs text-foreground-500">
                宇炁療育使用全站統一帳號，與 RG、OG、ADM 管理共用。任何使用者登入後都可建立服務申請；只有正式授權服務者能接受申請及建立正式案例。
              </p>
            </div>
            <QiAuthForm />
          </section>
        )}

        {/* Safety note */}
        <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 text-sm text-foreground-600 leading-relaxed">
          <div className="flex items-start gap-3">
            <i className="ri-shield-check-line text-accent-500 text-xl mt-0.5" />
            <div className="space-y-2">
              <p>
                本平台用於宇炁實踐、個人經驗回報與見證記錄，不取代醫療診斷、治療或緊急醫療服務。身體不適者仍應諮詢合格醫療專業人員。
              </p>
              <p>
                案例主訴、前後變化與影像均以當事人回報為準，系統不會自動改寫成醫療療效結論。
              </p>
            </div>
          </div>
        </section>

        {/* 案主 / 施炁者入口 */}
        {auth.user && (
          <section className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(role === 'practitioner' || role === 'candidate') && (
                <Link
                  to="/og/qi-healing/new"
                  className="flex items-center justify-center gap-2 whitespace-nowrap px-6 py-4 rounded-lg bg-accent-500 text-background-50 text-sm md:text-base hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-lg" />
                  建立施炁紀錄（為他人施炁）
                </Link>
              )}
              <Link
                to="/og/qi-healing/new?mode=owner"
                className="flex items-center justify-center gap-2 whitespace-nowrap px-6 py-4 rounded-lg bg-primary-500 text-background-50 text-sm md:text-base hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-user-heart-line text-lg" />
                建立我的宇炁案例
              </Link>
              <Link
                to="/og/qi-healing/witnesses"
                className="flex items-center justify-center gap-2 whitespace-nowrap px-6 py-4 rounded-lg bg-background-100 border border-foreground-200 text-foreground-700 text-sm md:text-base hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-eye-line text-lg" />
                匿名見證
              </Link>
            </div>

            {/* 回報連結入口 */}
            <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-4">
              <p className="text-xs text-foreground-500 mb-2">
                收到施炁者提供的回報連結？貼上連結或代碼，即可回報我的第一視角感受與後續追蹤。
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={reportToken}
                  onChange={(e) => setReportToken(e.target.value)}
                  placeholder="貼上回報連結或代碼"
                  className="flex-1 px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
                />
                <button
                  onClick={handleReportLink}
                  disabled={!reportToken.trim()}
                  className="whitespace-nowrap px-4 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
                >
                  前往回報
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Authenticated dashboard */}
        {auth.user && (
          <section className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                  <i className="ri-user-line text-sm text-background-50" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{auth.user.email || '已登入'}</p>
                  <p className="text-xs text-foreground-400">
                    {role === 'admin'
                      ? '系統管理員'
                      : role === 'reviewer'
                        ? '見證覆核者'
                        : role === 'practitioner'
                          ? '施炁者'
                          : role === 'candidate'
                            ? '準施炁者'
                            : '案主 / 受施者'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(role === 'admin' || role === 'reviewer') && (
                  <Link
                    to="/og/qi-healing/review"
                    className="whitespace-nowrap px-4 py-2 rounded-md bg-foreground-100 text-foreground-700 text-sm hover:bg-foreground-200 transition-colors cursor-pointer"
                  >
                    覆核後台
                  </Link>
                )}
                {role === 'admin' && (
                  <Link
                    to="/og/qi-healing/roles"
                    className="whitespace-nowrap px-4 py-2 rounded-md bg-foreground-100 text-foreground-700 text-sm hover:bg-foreground-200 transition-colors cursor-pointer"
                  >
                    角色管理
                  </Link>
                )}
                <button
                  onClick={auth.signOut}
                  className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-foreground-500 text-xs hover:bg-foreground-50 transition-colors cursor-pointer"
                >
                  登出
                </button>
              </div>
            </div>

            {(role === 'practitioner' || role === 'candidate') && <PractitionerProfile userId={auth.user.id} />}

            {/* 案主站內通知 */}
            {myNotifications.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                  待我回報
                </h2>
                <div className="space-y-2">
                  {myNotifications.map((n) => (
                    <Link
                      key={n.id}
                      to={`/og/qi-healing/case/${n.case_id}`}
                      className="block bg-accent-100/70 border border-accent-200 rounded-xl p-4 hover:border-accent-300 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <i className="ri-notification-3-line text-accent-600" />
                        <p className="text-sm font-medium text-foreground-800">施炁者邀請您補充案例</p>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-background-50 text-accent-700">{n.case_code || `案例 #${n.case_id}`}</span>
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">{n.recipient_alias || '受施者'} · {formatDate(n.created_at)}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* 施炁者全部暫存案例 */}
            {(role === 'practitioner' || role === 'candidate') && practitionerDraftCases.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                      暫存案例（可繼續填寫）
                    </h2>
                    <p className="text-xs text-foreground-500 mt-1">
                      所有尚未送出的案例都保留在這裡，可逐筆檢查後繼續下一步。
                    </p>
                  </div>
                  <span className="shrink-0 text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                    {practitionerDraftCases.length} 筆
                  </span>
                </div>
                <div className="space-y-2">
                  {practitionerDraftCases.map((draft) => (
                    <Link
                      key={draft.id}
                      to={`/og/qi-healing/new?draftId=${draft.id}`}
                      className="flex items-center justify-between gap-3 bg-background-100 border border-amber-200 rounded-xl p-4 hover:border-amber-400 transition-colors cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800 truncate">
                          {draft.recipient_alias || '未填受施者稱呼'}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1">
                          {draft.session_datetime ? formatDate(draft.session_datetime) : '尚未填寫施炁時間'} · 草稿 #{draft.id}
                        </p>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs text-amber-700 font-medium">
                        Review／繼續填寫
                        <i className="ri-arrow-right-line" />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* 尚未建立首筆案例的主訴系列 */}
            {(role === 'practitioner' || role === 'candidate') && pendingCaseSeries.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                      待建立案例（可繼續填寫）
                    </h2>
                    <p className="text-xs text-foreground-500 mt-1">
                      已保留受施者與主訴，但尚未建立第一次施炁紀錄；點入即可接續。
                    </p>
                  </div>
                  <span className="shrink-0 text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                    {pendingCaseSeries.length} 筆
                  </span>
                </div>
                <div className="space-y-2">
                  {pendingCaseSeries.map((series) => (
                    <Link key={series.id} to={`/og/qi-healing/new?seriesId=${series.id}`} className="flex items-center justify-between gap-3 bg-background-100 border border-amber-200 rounded-xl p-4 hover:border-amber-400 transition-colors cursor-pointer">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800 truncate">{series.recipient_alias || '未填受施者稱呼'}</p>
                        <p className="text-xs text-foreground-500 mt-1 line-clamp-2">主訴：{series.chief_complaint || '尚未填寫'}</p>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs text-amber-700 font-medium">Review／建立案例<i className="ri-arrow-right-line" /></span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* 施炁者 Dashboard */}
            {role === 'practitioner' && dashboard.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                  施炁者 Dashboard
                </h2>
                <div className="space-y-2">
                  {dashboard.map((row) => (
                    <Link
                      key={row.case_id}
                      to={`/og/qi-healing/case/${row.case_id}`}
                      className="block bg-background-100 border border-foreground-200/50 rounded-xl p-4 hover:border-accent-300 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{row.case_code || `案例 #${row.case_id}`}</p>
                          <p className="text-xs text-foreground-400 mt-0.5">
                            {row.recipient_alias || '未填稱呼'} · 施炁 {row.session_count} 次 · 最近 {formatDate(row.last_session_datetime)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${row.recipient_confirmed ? 'bg-emerald-100 text-emerald-700' : 'bg-background-200 text-foreground-600'}`}>
                            {row.recipient_confirmed ? '案主已補充確認' : '案主可後續補充（不擋送審）'}
                          </span>
                          {row.notification_status && (
                            <span className="text-xs px-2.5 py-1 rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">
                              通知：{row.notification_status === 'sent' ? '已送達' : row.notification_status === 'failed' ? '未寄出' : row.notification_status}
                            </span>
                          )}
                          {row.followup_pending_count > 0 && (
                            <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                              追蹤 {row.followup_pending_count} 項
                            </span>
                          )}
                        </div>
                      </div>
                      {row.next_followup_due && (
                        <p className="text-xs text-foreground-400 mt-2">下次追蹤：{row.next_followup_due}</p>
                      )}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                我的紀錄
              </h2>
              <Link
                to="/og/qi-healing/new?mode=owner"
                className="whitespace-nowrap text-sm text-accent-600 hover:text-accent-700 cursor-pointer transition-colors"
              >
                建立新案例 →
              </Link>
            </div>

            {loading ? (
              <div className="py-10 text-center">
                <div className="w-8 h-8 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="py-10 text-center space-y-3">
                <p className="text-sm text-rose-600">{error}</p>
                <button
                  onClick={() =>
                    auth.user && fetchMyCases(auth.user.id).then(setMyCases).catch(() => setError('載入失敗'))
                  }
                  className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 cursor-pointer"
                >
                  重試
                </button>
              </div>
            ) : ownerOnlyCases.length === 0 ? (
              <div className="py-10 text-center bg-background-100 border border-foreground-200/50 rounded-xl">
                <i className="ri-inbox-line text-2xl text-foreground-300" />
                <p className="text-sm text-foreground-500 mt-2">
                  {dashboard.length > 0 ? '同一案例已顯示於上方施炁者 Dashboard。' : '還沒有任何案例紀錄。'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {ownerOnlyCases.map((c) => (
                  <Link
                    key={c.id}
                    to={`/og/qi-healing/case/${c.id}`}
                    className="block bg-background-100 border border-foreground-200/50 rounded-xl p-4 hover:border-accent-300 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-sm font-medium text-foreground-800">
                          {c.case_code || `案例 #${c.id}`}
                        </p>
                        <p className="text-xs text-foreground-400 mt-0.5">
                          {c.recipient_alias || '未填稱呼'} &middot; {formatDate(c.session_datetime)}
                        </p>
                      </div>
                      <span className="text-xs px-2.5 py-1 rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">
                        {CASE_STATUS_LABELS[c.status] || c.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </OgShell>
  );
}
